/**
 * Two-column page layout used for both hotel and flight checkout.
 *
 * Left column ("secondary background"): header + booking summary. Right column
 * ("primary background"): the form. On mobile the summary collapses above
 * the form.
 */

import React from 'react';

import { ROUTES } from '@elara/client-core/constants';
import { PRIVACY_URL, TERMS_URL } from '@elara/domain/policy-versions';

import { useNavigate } from '#states/hooks.js';
import { Button, IconSizes, Logo, MaterialIcon } from '#ui/index.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '#ui/tooltip.js';
import { Link } from '#views/Router.js';

interface CheckoutLayoutProps {
  /** Form column. */
  form: React.ReactNode;
  /** Right-rail summary column. */
  summary: React.ReactNode;
  /** Optional total displayed next to "View booking summary" on mobile. */
  summaryTotal?: string;
}

/** Caps each column's content width on wide viewports (half of max-w-screen-xl,
    1280px/2) while letting each column's background bleed to the viewport edge. */
const HALF_MAX_W = 'max-w-[720px]';

export function CheckoutLayout({ form, summary, summaryTotal }: CheckoutLayoutProps): React.ReactNode {
  const { goBack } = useNavigate();

  const header = (
    <header className="relative flex items-center gap-2 px-4 py-3 md:px-16 md:py-0">
      {/* Desktop: icon-only back button in the gutter, left of the logo.
          Goes back in browser history so the user lands wherever they
          came from (trip view, deep link, etc.) rather than a fixed
          destination. */}
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="icon"
              size="icon-sm"
              onClick={goBack}
              className="hidden md:absolute md:inline-flex md:left-5 md:top-1/2 md:-translate-y-1/2 opacity-50 hover:opacity-100"
              aria-label="Back"
            >
              <MaterialIcon icon="arrow_back" size={IconSizes.sm} className="text-muted-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Back</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Link to={ROUTES.HOME} className="shrink-0">
        <Logo />
      </Link>

      {/* Mobile: text link on the right. */}
      <Button variant="link" size="sm" onClick={goBack} className="ml-auto md:hidden">
        Back
      </Button>
    </header>
  );

  const footer = (
    <footer className="flex items-center gap-4 text-xs text-muted-foreground">
      <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="hover:text-foreground hover:underline">
        Terms of Service
      </a>
      <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="hover:text-foreground hover:underline">
        Privacy Policy
      </a>
    </footer>
  );

  return (
    <div className="flex min-h-screen flex-col bg-background md:bg-background-secondary">
      {/* Mobile: stack with collapsible summary. The whole page scrolls. */}
      <div className="flex flex-col md:hidden">
        {header}
        <details className="border-y border-border bg-background-secondary px-4 py-3">
          <summary className="flex cursor-pointer items-baseline justify-between gap-4 text-sm font-medium">
            <span>View booking summary</span>
            {summaryTotal ? <span>{summaryTotal}</span> : null}
          </summary>
          <div className="mt-3">{summary}</div>
        </details>
        <main className="flex-1 px-4 py-6">{form}</main>
        <div className="px-4 py-4">{footer}</div>
      </div>

      {/* Desktop: 50/50 columns. The PAGE itself scrolls — the form column
          flows naturally inside the document, and the left column is pinned
          via `position: sticky; top: 0; height: 100vh`. That way scrolling
          anywhere on the page (including over the left column) moves the
          form column.
          The aside itself is also scrollable: when its content fits inside
          the viewport `mt-auto` on the footer pushes it to the bottom, and
          when the content (header + summary + footer + padding) is taller
          than the viewport the aside scrolls internally so the user can
          still reach everything. */}
      <div className="hidden flex-1 md:flex">
        <aside className="sticky top-0 flex h-screen flex-1 flex-col gap-12 overflow-y-auto py-12">
          <div className={`w-full ${HALF_MAX_W} self-end`}>{header}</div>
          <div className="flex justify-end">
            <div className={`w-full ${HALF_MAX_W} px-8 lg:px-16`}>{summary}</div>
          </div>
          <div className={`mt-auto w-full ${HALF_MAX_W} self-end px-8 lg:px-16`}>{footer}</div>
        </aside>
        <main className="flex flex-1 justify-start bg-background md:shadow-sm">
          {/* Doubled bottom padding gives the submit button breathing room
              before the page bottom. */}
          <div className={`w-full ${HALF_MAX_W} px-8 lg:px-16 pt-32 pb-32`}>{form}</div>
        </main>
      </div>
    </div>
  );
}
